import type { ProjectPlanRevision, TilesMeta } from "@/types/api";

/** Locked contract: tile URL = `${base_url}/${level}/${x}_${y}.${format}`. */
export function tileUrlFor(tiles: TilesMeta) {
  return (level: number, x: number, y: number): string =>
    `${tiles.base_url}/${level}/${x}_${y}.${tiles.format}`;
}

export interface XY {
  x: number;
  y: number;
}

/** A box in normalized `[0,1]` plan coordinates. */
export interface NormalizedBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * `fitToBounds` pads the box by this fraction of its larger side on every
 * side, so a tight group of pins is framed with room around it rather than
 * magnified until it fills the edges.
 */
export const AREA_PADDING_RATIO = 0.5;

/**
 * …and never by less than this fraction of the image width, so a box of one
 * point (or a hair-thin line of them) still gets a sensible frame.
 */
export const AREA_PADDING_FLOOR_RATIO = 0.01;

/**
 * A point is "comfortably visible" while it is further than this fraction of
 * the visible area's smaller side from every edge; `ensureVisible` only pans
 * once it is not.
 */
export const REVEAL_INSET_RATIO = 0.15;

/**
 * `bounds` grown by the framing padding, still normalized (it may reach past
 * `[0,1]`; the surfaces clamp). Worked out in image pixels — normalized x and y
 * are different lengths on a non-square sheet — so the padding is the same
 * distance on both axes. Shared by this surface's `fitToBounds` and the legacy
 * surface's equivalent in the plan viewer. See [[tiled-plan-viewer.spec]].
 */
export function paddedBounds(
  bounds: NormalizedBounds,
  imageWidth: number,
  imageHeight: number,
): NormalizedBounds {
  if (!(imageWidth > 0) || !(imageHeight > 0)) return bounds;
  const widthPx = (bounds.maxX - bounds.minX) * imageWidth;
  const heightPx = (bounds.maxY - bounds.minY) * imageHeight;
  const padPx = Math.max(
    AREA_PADDING_RATIO * Math.max(widthPx, heightPx),
    AREA_PADDING_FLOOR_RATIO * imageWidth,
  );
  const padX = padPx / imageWidth;
  const padY = padPx / imageHeight;
  return {
    minX: bounds.minX - padX,
    minY: bounds.minY - padY,
    maxX: bounds.maxX + padX,
    maxY: bounds.maxY + padY,
  };
}

/** Pin coords are stored normalized [0,1] — multiply out to image px. */
export function normalizedToImage(pin: XY, tiles: TilesMeta): XY {
  return { x: pin.x * tiles.width, y: pin.y * tiles.height };
}

/** Image px → normalized [0,1]; null when outside the sheet (rejected tap). */
export function imageToNormalized(point: XY, tiles: TilesMeta): XY | null {
  const x = point.x / tiles.width;
  const y = point.y / tiles.height;
  if (x < 0 || x > 1 || y < 0 || y > 1) return null;
  return { x, y };
}

/**
 * Viewer-selection seam: OSD only when the pipeline has finished AND the
 * metadata is present; every other state (pending/processing/failed/legacy/
 * absent) stays on the `<img>` + react-zoom-pan-pinch path untouched.
 * Callers add their own online gate where offline parity matters
 * (see [[plan-viewer.spec]] — tiles are online-first in v1).
 */
export function isTiledReady(
  revision: Pick<ProjectPlanRevision, "tiles" | "tile_status"> | null | undefined,
): boolean {
  return revision?.tiles != null && revision.tile_status === "ready";
}

/**
 * True while the server pipeline is still rendering this revision's pyramid
 * (`pending` or `processing`) — the states that drive the "Sharpening…"
 * badges and the plans-list polling window. `failed`/`ready`/legacy/absent
 * are all false.
 */
export function isTileProcessing(
  revision: Pick<ProjectPlanRevision, "tile_status"> | null | undefined,
): boolean {
  return (
    revision?.tile_status === "pending" ||
    revision?.tile_status === "processing"
  );
}
